import { Box, Typography, keyframes } from "@mui/material";
import { alpha } from "@mui/material";
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SpeedIcon from '@mui/icons-material/Speed';
import SecurityIcon from '@mui/icons-material/Security';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const benefits = [
  {
    icon: SpeedIcon,
    title: "10x mais rápido",
    description: "Importe milhares de parcelas em segundos",
  },
  {
    icon: AutoAwesomeIcon,
    title: "Detecção inteligente",
    description: "Identificação automática de parcelas e valores",
  },
  {
    icon: SecurityIcon,
    title: "Validação automática",
    description: "Verificação de dados antes da importação",
  },
  {
    icon: TrendingUpIcon,
    title: "Relatórios em tempo real",
    description: "Acompanhe métricas de recebimentos",
  },
];

export const ComingSoonOverlay = () => {
  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 10,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: (theme) => alpha(theme.palette.background.default, 0.85),
        backdropFilter: "blur(6px)",
        borderRadius: 3,
        padding: 3,
      }}
    >
      <Box
        sx={{
          maxWidth: 600,
          width: "100%",
          textAlign: "center",
        }}
      >
        {/* Icon */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 2,
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              animation: `${float} 3s ease-in-out infinite`,
              boxShadow: (theme) => `0 16px 32px ${alpha(theme.palette.primary.main, 0.25)}`,
            }}
          >
            <RocketLaunchIcon sx={{ fontSize: 40, color: "white" }} />
          </Box>
        </Box>

        {/* Title */}
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            mb: 1.5,
            background: (theme) => `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
            backgroundSize: "200% auto",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            animation: `${shimmer} 3s linear infinite`,
          }}
        >
          Algo incrível está chegando!
        </Typography>

        {/* Subtitle */}
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            mb: 3,
            maxWidth: 450,
            mx: "auto",
            lineHeight: 1.6,
          }}
        >
          Estamos finalizando a nova ferramenta de{" "}
          <Typography component="span" sx={{ color: "primary.main", fontWeight: 600 }}>
            importação automática de parcelas
          </Typography>{" "}
          que vai revolucionar sua gestão de comissões.
        </Typography>

        {/* Benefits Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 1.5,
            mb: 3,
          }}
        >
          {benefits.map((benefit) => (
            <Box
              key={benefit.title}
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                textAlign: "left",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  transform: "translateY(-2px)",
                  boxShadow: (theme) => `0 6px 16px ${alpha(theme.palette.primary.main, 0.12)}`,
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <Box
                  sx={{
                    p: 0.8,
                    borderRadius: 1.5,
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                    color: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <benefit.icon sx={{ fontSize: 18 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", mb: 0.25 }}>
                    {benefit.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {benefit.description}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>

        {/* CTA Section */}
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            background: (theme) => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.secondary.main, 0.08)})`,
            border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: "0.95rem", mb: 0.5 }}>
            Fique tranquilo, você será um dos primeiros a usar!
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Estamos trabalhando para entregar a melhor experiência possível.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
